/**
 * Publishing the certificate to the member's own LinkedIn feed, from the share dialog.
 *
 * The whole feature exists only on a server-rendered public page: the API origin is taken from the
 * address the page's own `og:image` already names, so the dialog cannot reach for an API that this
 * document does not itself point at, and the serverless demo — which has no such tag — simply never
 * offers any of it.
 *
 * WHAT NEVER TOUCHES THIS FILE
 * ----------------------------
 * No access token, no client secret, no authorization code. The browser's whole vocabulary is a
 * draft id, a draft secret and a session token, each opaque and each kept in React state for the
 * life of the dialog — never in `localStorage`, never in `sessionStorage`, never in a URL this code
 * writes. The draft secret in particular never leaves the tab that created it, which is what stops
 * a callback address seen elsewhere from being enough to post as this member.
 *
 * WHY A POPUP, AND WHY IT IS NOT TRUSTED TO TALK BACK
 * ---------------------------------------------------
 * Sending the whole tab to LinkedIn would destroy the draft secret held in memory, and putting it
 * somewhere that survives navigation would mean storing it. So the authorization runs in a second
 * window. That window does post a one-time value back, checked against the API origin — but it is
 * only an accelerator: the tab also ASKS the server whether the authorization has completed, and
 * that is what actually opens the session. A browser that severs the opener, or a phone that turns
 * the popup into a plain tab, would otherwise leave the member watching a spinner forever.
 *
 * Nothing is published on the way back: the member returns to this dialog and presses publish.
 */

export interface MemberPostDraft {
  draftId: string;
  draftSecret: string;
  canonicalUrl: string;
  commentaryMaxLength: number;
}

export interface MemberPostSession {
  sessionToken: string;
  memberName: string;
}

export interface PublishedPost {
  postId: string;
  postUrl: string;
  alreadyPublished: boolean;
}

/** Named outcomes the dialog can speak about. A status code never reaches the reader. */
export type MemberPostFailure =
  | 'not_configured'
  | 'popup_blocked'
  | 'denied'
  /**
   * The return from LinkedIn could not be tied back to the window that started it.
   *
   * NOT a statement about which browser anyone used. The cookie that does the tying can be missing
   * for reasons nobody chose — a privacy setting, a cleared jar, a start that never completed — and
   * the only thing this establishes is that nothing was connected.
   */
  | 'binding_unverified'
  | 'expired'
  /**
   * The API refused the start navigation itself, before any ticket was spent.
   *
   * Kept apart from `expired` because it is the opposite situation: the ticket was minted a moment
   * earlier and is fine; what the API could not establish is that the navigation came from this
   * deployment's own page. Telling a member their request had expired sent them straight back to
   * the press that produces it again.
   */
  | 'start_refused'
  | 'unavailable'
  /**
   * The wait ran out with no answer either way.
   *
   * Deliberately NOT `denied`. A refusal is something LinkedIn's own answer said; a time-out is
   * this application running out of patience, and the two need different words because they need
   * different next steps. Nothing about a window handle ever produces `denied`.
   */
  | 'timed_out'
  /** The server asked us to slow down. Carried so the caller can wait rather than give up. */
  | 'rate_limited'
  | 'result_unknown'
  /**
   * The server refused to start the share, and nothing was sent to LinkedIn.
   *
   * Its own precondition — a durable record that an attempt began — could not be written, so it
   * stopped before the provider rather than creating a post it would be unable to account for. No
   * post exists, the idempotency key is free again, and pressing publish once more is the whole
   * remedy. Deliberately NOT `failed`: that word covers endings where the member has nothing better
   * to do than give up, and this one is a moment that passes.
   */
  | 'not_started'
  /**
   * The attempt was abandoned by this application — the dialog closed, or a new attempt replaced it.
   *
   * Not a refusal and not a failure: nothing is shown for it. It exists so an abandoned attempt can
   * STOP rather than keep polling, keep listening for a hand-over meant for the attempt that came
   * after it, and then write its answer over that attempt's state.
   */
  | 'cancelled'
  | 'failed';

export class MemberPostError extends Error {
  constructor(
    readonly failure: MemberPostFailure,
    /** Seconds the server asked us to wait, when it said so. */
    readonly retryAfterSeconds = 0,
  ) {
    super(failure);
    this.name = 'MemberPostError';
  }
}

/**
 * The API this document itself points at, or null.
 *
 * Reading it from `og:image` rather than from a build-time constant means the dialog talks to the
 * same origin the page already told the crawler about, and that a page with no server-rendered head
 * — the demo — gets no integration at all rather than a broken one.
 */
export function apiOriginFromDocument(): string | null {
  if (typeof document === 'undefined') return null;
  const image = document.head?.querySelector('meta[property="og:image"]')?.getAttribute('content');
  if (!image) return null;
  try {
    const url = new URL(image);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : null;
  } catch {
    return null;
  }
}

async function call<T>(url: string, init: RequestInit & { idempotencyKey?: string } = {}): Promise<T> {
  const { idempotencyKey, ...rest } = init;
  let res: Response;
  try {
    res = await fetch(url, {
      ...rest,
      /*
       * Anonymous, without exception. The browser binding is NOT established here: a cookie set on
       * a cross-site fetch is a third-party cookie and a browser that blocks those drops it. It is
       * established by the window's own top-level navigation to the API instead — see `connectMember`.
       */
      credentials: 'omit',
      cache: 'no-store',
      headers: {
        'content-type': 'application/json',
        ...(idempotencyKey ? { 'idempotency-key': idempotencyKey } : {}),
        ...(rest.headers ?? {}),
      },
    });
  } catch {
    /* The request did not complete. For a publish that is genuinely unknown, so it is never
       retried automatically; the dialog says so and lets the member decide. */
    throw new MemberPostError('result_unknown');
  }
  if (res.ok) return (await res.json()) as T;
  /*
   * Being asked to slow down is not a failure of the flow. The connect poll repeats while the
   * member is away at LinkedIn, and reading a 429 as a general error used to end the whole
   * attempt in the middle of a perfectly normal authorization. The server's own `Retry-After` is
   * what decides how long to wait — never a number invented here.
   */
  if (res.status === 429) {
    const header = Number(res.headers.get('retry-after'));
    throw new MemberPostError('rate_limited', Number.isFinite(header) && header > 0 ? header : 1);
  }
  let code = '';
  try {
    code = ((await res.json()) as { error?: { code?: string } }).error?.code ?? '';
  } catch {
    code = '';
  }
  if (code === 'linkedin_session_expired') throw new MemberPostError('expired');
  if (code === 'linkedin_permission_missing') throw new MemberPostError('denied');
  /*
   * The two answers that used to be missing. A finished-and-failed authorization now says so, and
   * the poll ends on them instead of reading "no session yet" and waiting out the whole flow.
   */
  /* The start navigation itself was refused: nothing was opened, so there is nothing to wait for
     and nothing to undo. Its own ending, because its remedy is its own. */
  if (code === 'linkedin_start_refused') throw new MemberPostError('start_refused');
  if (code === 'linkedin_binding_unverified') throw new MemberPostError('binding_unverified');
  if (code === 'linkedin_authorization_failed') throw new MemberPostError('failed');
  if (code === 'linkedin_result_unknown') throw new MemberPostError('result_unknown');
  if (code === 'linkedin_not_configured') throw new MemberPostError('not_configured');
  if (code === 'linkedin_share_not_available') throw new MemberPostError('unavailable');
  if (code === 'linkedin_draft_unknown' || code === 'linkedin_handoff_unknown')
    throw new MemberPostError('expired');
  if (code === 'linkedin_publish_in_progress') throw new MemberPostError('failed');
  /* Nothing reached LinkedIn. Said as its own ending so the dialog can offer the retry it deserves. */
  if (code === 'linkedin_share_not_started') throw new MemberPostError('not_started');
  throw new MemberPostError('failed');
}

/**
 * The API origin this page should talk to — from the head, or from the build.
 *
 * `apiOriginFromDocument` reads `og:image`, which is the address the server itself composed, so it
 * cannot disagree with what the crawler was given. But that tag is only written when the server's
 * own record read succeeded: `frontend/server.mjs` time-boxes it at 2.5 s and falls back to
 * `GENERIC_TAGS`, which carry no `og:image` at all. On such a response the origin came back null
 * and the publish flow turned itself off — a LinkedIn integration silently disabled by an
 * unrelated timeout, leaving only the link share behind with nothing said.
 *
 * So the build's own `VITE_API_BASE_URL` stands behind it. Only an EXPLICITLY configured value:
 * `readEnvConfig` falls back to `window.location.origin` when it is unset, and that default would
 * make the demo — which has no API at all — start asking itself for a LinkedIn status.
 */
function configuredApiOrigin(): string | null {
  const raw = (import.meta.env?.VITE_API_BASE_URL ?? '').trim();
  if (!raw) return null;
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : null;
  } catch {
    return null;
  }
}

export function memberPostApiOrigin(): string | null {
  return apiOriginFromDocument() ?? configuredApiOrigin();
}

/**
 * What the deployment answered about the integration — three answers, not one.
 *
 * `enabled` is the integration configured and live. `disabled` is the deployment saying, with a
 * perfectly good 200, that it is not configured; that is a real product state and the dialog is
 * right to offer the link share instead. `unreachable` is everything else — a 4xx or 5xx, a rate
 * limit, a CORS refusal, a dropped connection — and it is NOT the same thing, because nothing was
 * established about the integration at all.
 *
 * All three used to be `false`. A member whose status request failed for a second was shown the
 * link-only dialog as though that were the product, and the operator had no way to tell a missing
 * `LINKEDIN_SHARE_ENABLED` from an API that did not answer.
 */
export type MemberPostAvailability = 'enabled' | 'disabled' | 'unreachable';

export async function memberPostAvailability(apiOrigin: string): Promise<MemberPostAvailability> {
  let res: Response;
  try {
    res = await fetch(`${apiOrigin}/api/v1/public/linkedin/status`, {
      credentials: 'omit',
      cache: 'no-store',
    });
  } catch {
    return 'unreachable';
  }
  if (!res.ok) return 'unreachable';
  try {
    const body = (await res.json()) as { memberPostEnabled?: boolean };
    /* A 200 whose body does not carry the flag is not a deployment saying «off»; it is a body this
       client does not understand, which is a reachability problem, not a configuration answer. */
    if (typeof body.memberPostEnabled !== 'boolean') return 'unreachable';
    return body.memberPostEnabled ? 'enabled' : 'disabled';
  } catch {
    return 'unreachable';
  }
}

export async function openDraft(
  apiOrigin: string,
  publicCode: string,
  text: string,
): Promise<MemberPostDraft> {
  return call<MemberPostDraft>(
    `${apiOrigin}/api/v1/public/credentials/${encodeURIComponent(publicCode)}/linkedin/drafts`,
    { method: 'POST', body: JSON.stringify({ text }) },
  );
}

/** The name the authorization window is opened under, so the start form can target it. */
export const AUTHORIZATION_WINDOW_NAME = 'hpw-linkedin';

/**
 * Sends the authorization window to the API, at the top level, carrying the one-time ticket.
 *
 * WHY A FORM AND NOT `location.replace`
 * -------------------------------------
 * Two things have to be true at once. The navigation must be TOP-LEVEL on the API's own origin,
 * because that is the only context in which the API can set a cookie that a browser blocking
 * third-party cookies will keep — the previous version set it on a cross-site `fetch`, which is by
 * definition third-party, and members with that blocking on came back from LinkedIn with no cookie
 * at all. And the ticket must not travel in the address bar, so it cannot be read out of history,
 * a screenshot, a referrer or a shoulder. A form POST is both: a top-level navigation whose value
 * is in the body.
 *
 * The form is built, submitted and removed inside this call. Nothing is stored.
 */
function submitStart(apiOrigin: string, ticket: string, draftSecret: string): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = `${apiOrigin}/api/v1/public/linkedin/oauth/start`;
  form.target = AUTHORIZATION_WINDOW_NAME;
  form.hidden = true;
  for (const [name, value] of [
    ['t', ticket],
    /* The secret that never left this tab. It proves the window being sent belongs to the draft,
       so a ticket carried elsewhere cannot attach that browser's account to this one. It travels
       in the body: not in the address bar, not in storage, not in any log. */
    ['s', draftSecret],
  ]) {
    const field = document.createElement('input');
    field.type = 'hidden';
    field.name = name as string;
    field.value = value as string;
    form.appendChild(field);
  }
  document.body.appendChild(form);
  try {
    form.submit();
  } finally {
    form.remove();
  }
}

/**
 * Runs the authorization in a second window and waits for the connection to complete.
 *
 * The window is opened synchronously inside the click that asked for it — a popup opened after an
 * `await` is the one browsers refuse — and is then sent, by name, to the API's own start route,
 * which sets the binding cookie first-party and forwards it to LinkedIn.
 */
export async function connectMember(
  apiOrigin: string,
  draft: MemberPostDraft,
  popup: Window | null,
  options: {
    /** The navigation is injectable so a test can watch it; the default is the real form submit. */
    startAuthorization?: (apiOrigin: string, ticket: string, draftSecret: string) => void;
    /**
     * Ends the attempt when the caller abandons it.
     *
     * WHY AN ABANDONED ATTEMPT HAD TO BE ABLE TO STOP
     * -----------------------------------------------
     * This loop used to run to its own five-minute deadline no matter what. Closing the share
     * dialog did not end it: it kept polling, and — the part that actually broke the next attempt —
     * it kept its `message` listener registered. So when the member opened the dialog again and
     * connected a second time, the callback's one-time hand-over was received by BOTH the live
     * attempt and the abandoned one, and whichever asked first SPENT it. The hand-over is
     * single-use, so the attempt the member was watching was left waiting for a value that had
     * already been redeemed against a draft nobody could see any more.
     *
     * With a signal, the caller ends the old attempt before it starts the new one, and a late
     * answer from the old one can no longer reach the new one's state.
     */
    signal?: AbortSignal;
  } = {},
): Promise<MemberPostSession> {
  if (!popup) throw new MemberPostError('popup_blocked');
  const signal = options.signal;
  if (signal?.aborted) throw new MemberPostError('cancelled');
  let startTicket: string;
  try {
    ({ startTicket } = await call<{ startTicket: string }>(`${apiOrigin}/api/v1/public/linkedin/authorize`, {
      method: 'POST',
      body: JSON.stringify({ draftId: draft.draftId, draftSecret: draft.draftSecret }),
    }));
  } catch (e) {
    /*
     * The only window this flow ever closes. It is still blank — it has not been pointed anywhere
     * yet, so there is nothing on it for anyone to read. Once it carries the authorization, and
     * then the callback's account of how that ended, it is left alone.
     */
    popup.close();
    throw e;
  }
  (options.startAuthorization ?? submitStart)(apiOrigin, startTicket, draft.draftSecret);

  /*
   * The tab asks the server whether the authorization has completed, and keeps asking while the
   * member is away. A one-time value posted back from the callback, when the browser manages to
   * carry one, only makes the answer arrive sooner: relying on that message alone would strand
   * every member whose browser severs the opener or finishes in a plain tab, which on a phone is
   * most of them.
   */
  let fast: string | null = null;
  let ended: MemberPostFailure | null = null;
  /* Wakes the wait immediately rather than at the end of the current poll interval. */
  let wake: (() => void) | null = null;
  const onAbort = (): void => {
    ended = 'cancelled';
    wake?.();
  };
  signal?.addEventListener('abort', onAbort);
  const onMessage = (event: MessageEvent): void => {
    /* The origin check is the whole point: a message from anywhere else is not an answer. */
    if (event.origin !== apiOrigin) return;
    const data = event.data as { source?: string; status?: string; handoff?: string } | null;
    if (data?.source !== 'hpw-linkedin') return;
    if (data.status === 'ok' && data.handoff) fast = data.handoff;
    else if (data.status === 'denied') ended = 'denied';
    else if (data.status === 'binding_unverified') ended = 'binding_unverified';
    /* Every ending the callback page can report, not just the two that were listed. An
       unhandled one used to leave the dialog waiting for an answer that had already arrived. */ else if (
      data.status === 'failed' ||
      data.status === 'state_invalid'
    )
      ended = 'failed';
  };
  window.addEventListener('message', onMessage);

  /*
   * WHAT THE WINDOW HANDLE IS NOT EVIDENCE OF
   * -----------------------------------------
   * `popup.closed` is not read here at all, and that is deliberate. A page served with
   * `Cross-Origin-Opener-Policy: same-origin` severs the opener relationship, after which the
   * handle reads `closed === true` while the window is plainly still open and the member is still
   * deciding — proven in Chromium by the delayed check, where it flips within five seconds of a
   * thirty-five second authorization. An earlier version treated it as a refusal outright; the
   * version after that made it start a ninety-second grace, which is the same mistake with a
   * timer on it — a member who took two minutes was told at ninety-one seconds that they had
   * refused, seconds before the connection would have completed.
   *
   * There is exactly one thing a window handle can tell you, and it is not consent. So the only
   * refusals this loop reports are the ones LinkedIn's own answer produced, relayed by the
   * callback page from the API origin. Everything else that runs out is a TIME-OUT, which is a
   * different outcome with a different message: "nothing arrived" is not "you said no".
   */
  const started = Date.now();
  const DEADLINE_MS = 5 * 60_000;
  const POLL_MS = 1500;
  const deadlineAt = started + DEADLINE_MS;

  /** One question to the server, and what to do about the answer — never how long to sleep. */
  type Answer =
    | { kind: 'session'; session: MemberPostSession }
    | { kind: 'waiting' }
    | { kind: 'slow_down'; seconds: number };

  const askServer = async (): Promise<Answer> => {
    try {
      const opened = await call<{ sessionToken: string; member: { name: string } }>(
        `${apiOrigin}/api/v1/public/linkedin/sessions`,
        {
          method: 'POST',
          body: JSON.stringify({
            draftId: draft.draftId,
            draftSecret: draft.draftSecret,
            ...(fast ? { handoff: fast } : {}),
          }),
        },
      );
      return {
        kind: 'session',
        session: { sessionToken: opened.sessionToken, memberName: opened.member.name },
      };
    } catch (e) {
      if (!(e instanceof MemberPostError)) throw e;
      /*
       * "Not connected yet" is the normal answer while the member is still at LinkedIn — and it is
       * now ONLY that. A refusal, a rejected identity or a failed exchange arrive as their own
       * codes above and come through here as themselves, ending the wait at once.
       */
      if (e.failure === 'expired') return { kind: 'waiting' };
      /* Being asked to slow down is an instruction. It is reported, not acted on here, because
         the caller is the one that knows what the deadline is. */
      if (e.failure === 'rate_limited') return { kind: 'slow_down', seconds: e.retryAfterSeconds };
      throw e;
    }
  };

  try {
    for (;;) {
      if (ended) throw new MemberPostError(ended);
      if (Date.now() >= deadlineAt) throw new MemberPostError('timed_out');

      const answer = await askServer();
      /* Abandoned WHILE the request was in flight: its answer belongs to nobody now, and opening a
         session on it would hand a connection to a dialog that is no longer on screen. */
      if (ended === 'cancelled') throw new MemberPostError('cancelled');
      if (answer.kind === 'session') return answer.session;
      if (ended) throw new MemberPostError(ended);

      /*
       * The server's wait is obeyed in full. Truncating it — an earlier version capped it at
       * thirty seconds — means sending a request the server has already refused and told you not
       * to send yet, which earns another refusal and makes the limiter's job harder. If waiting
       * that long would run past the deadline, the honest answer is the time-out, not an early
       * request that cannot help.
       */
      const waitMs = answer.kind === 'slow_down' ? Math.max(1, answer.seconds) * 1000 : POLL_MS;
      if (Date.now() + waitMs > deadlineAt) throw new MemberPostError('timed_out');
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, waitMs);
        wake = () => {
          clearTimeout(timer);
          resolve();
        };
      });
      wake = null;
    }
  } finally {
    window.removeEventListener('message', onMessage);
    signal?.removeEventListener('abort', onAbort);
  }
}

/**
 * Publishes, with an idempotency key the caller keeps for the whole attempt.
 *
 * The key is what makes a double click harmless: the same key returns the first post rather than
 * making a second one. It is deliberately NOT regenerated on a retry after an unknown result — that
 * case is the member's to decide, not the code's.
 */
export async function publishPost(
  apiOrigin: string,
  draft: MemberPostDraft,
  session: MemberPostSession,
  text: string,
  idempotencyKey: string,
): Promise<PublishedPost> {
  return call<PublishedPost>(`${apiOrigin}/api/v1/public/linkedin/publish`, {
    method: 'POST',
    idempotencyKey,
    body: JSON.stringify({
      draftId: draft.draftId,
      draftSecret: draft.draftSecret,
      sessionToken: session.sessionToken,
      text,
    }),
  });
}

/** A fresh key per publish attempt. Random, never derived from anything about the member. */
export function newIdempotencyKey(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * A connection this TAB has already made, for the life of this tab.
 *
 * WHY IT EXISTS
 * -------------
 * The draft and the session used to live in the share dialog's own component state, so closing the
 * dialog without publishing threw them away. Reopening it left the member facing «LinkedIn hesabını
 * bağla» again with an account that was, on the server, still perfectly connected — and pressing it
 * made things worse rather than better: `startLinkedInAuthorization` stands the previous connection
 * down when a new attempt begins, so the second press disconnected the first connection before the
 * second one existed.
 *
 * WHAT THIS IS, AND WHAT IT IS NOT
 * --------------------------------
 * It is a module-level `Map`: JavaScript memory in one tab, gone when the tab is closed or
 * reloaded, never shared with another tab and never readable by a server. It is deliberately NOT
 * `localStorage`, NOT `sessionStorage`, NOT IndexedDB and NOT a cookie: the draft secret is what
 * proves this tab owns the draft, and putting it anywhere that survives navigation would be
 * storing a secret.
 *
 * Keyed by public code, so opening a different record's dialog never finds another record's draft.
 */
export interface RememberedConnection {
  draft: MemberPostDraft;
  session: MemberPostSession | null;
  /** Kept so reopening the dialog cannot publish a second time under a new key. */
  idempotencyKey: string | null;
  /** So a record already published in this tab reopens showing that, rather than offering it again. */
  published: PublishedPost | null;
}

const connections = new Map<string, RememberedConnection>();

export function rememberedConnection(publicCode: string): RememberedConnection | null {
  return connections.get(publicCode) ?? null;
}

export function rememberConnection(publicCode: string, value: RememberedConnection): void {
  connections.set(publicCode, value);
}

/** Dropped when the server will not accept the draft again — an expired or repudiated one. */
export function forgetConnection(publicCode: string): void {
  connections.delete(publicCode);
}

/** Tests only: no product path clears every record's connection at once. */
export function forgetAllConnections(): void {
  connections.clear();
}
